/* ============================================================
   script-product.js — unified, variant-aware PDP script.

   Replaces the split script-product-standard.js / -variants.js. Owns
   the shared PDP behaviour:
     • the horizontal-scroll progress indicator
     • image-row navigation: drag to scroll, click right 75% / left 25% → next /
       previous image, fading around at either end
     • variant selection (SKU title / price / add-to-cart id / images)
     • the collection "family" SKU row: prefetch siblings on open, and
       soft-swap to a sibling on click (no reload) keeping the selected
       variant (colour) where the target product has a matching one.

   The bottom panel is a normal in-flow element below the image area; the
   page scrolls it into view (no JS pinning/drag).
   ============================================================ */

(function () {
  "use strict";

  var panelEl = null;
  var scrollerEl = null;
  var fillEl = null;
  var variantData = {};         // current product's variants, keyed by id
  var lastVariantTitle = null;  // selected variant title — preserved across swaps
  var prefetchCache = {};       // url -> Promise<htmlString | null>
  var warmedImg = {};           // srcset|src -> true (family images already warmed)
  var windowListenersBound = false;
  var videoListenersBound = false;   // instruction-video click→fullscreen delegation
  var currentVariantId = null;  // selected variant id (single-variant: the only id)
  var currentCart = null;       // predicted cart from script-cart.js (cart:updated)
  var pendingAdds = {};         // variantId -> units submitted but not yet in currentCart
  var pendingBase = {};         // variantId -> cart-only qty when that hold was taken
  var buttonState = "add";      // last state applyButtonState resolved ("add"|"sold"|"incart")
  var indicatorIntroDone = false; // first fill measurement after open → grow it in (see revealIndicator)
  var indicatorMeasuring = false; // a variant image set is still decoding — hold the fill (see refreshIndicator)
  var indicatorEl = null;         // the .product-scroll-indicator track (opacity auto-hides on idle)
  var indicatorIdleTimer = null;  // fades the indicator out after INDICATOR_IDLE_MS of no scroll
  var INDICATOR_IDLE_MS = 1875; // 1.5s + 25%
  var indicatorSwitchTimer = null; // pending re-measure after an image-set change
  var pageWasAwayFromTop = false; // last page-scroll position was below PAGE_TOP_SLOP
  var sizeObserver = null;      // watches the scroller relayout so the fill re-measures
  var ADVANCE_ZONE = 0.75;      // right 75% of the image area → next image; left → previous
  var DRAG_SLOP = 5;            // px of pointer travel that turns a click into a drag
  /* Wrap-around fade — mirrors the logo→grid exit out of a PDP (see wrapScroll). */
  var WRAP_FADE_OUT_MS = 100;   // closeProductSwap's 0.10s ease-out
  var WRAP_FADE_IN_MS = 900;    // pmFadeInView's 0.9s ease-in-out
  var rowFading = false;        // the image row is mid content-change fade
  var rowFadeTimer = null;      // id of the running fade's cleanup timer
  var wrapFading = false;       // ...and that fade owns the indicator too (wrap only)
  var STEP_SCROLL_MS = 315;     // click-to-step travel time, TOUCH ONLY (stepScrollTo)
  var stepAnimFrame = null;     // rAF id of the running step animation
  var stepPendingTarget = null; // where an in-flight step is headed (null when idle)
  var stepPendingTimer = null;  // releases stepPendingTarget after a native scroll

  /* ---------- compact (stacked) peek switch ----------

     The peek bar stacks once the product image is as wide as the browser. The
     image is sized off the viewport HEIGHT — 70.125% of (viewport − peek) — and
     then capped at 100vw, so the window width at which it fills the viewport
     depends on that image's aspect ratio and on the window height. A media query
     can't express that, so we measure it and toggle body.pdp-compact; the compact
     layout itself lives in style-product.css.

     The prediction deliberately uses the DESKTOP peek constant even while compact
     is active. Measuring the live image instead would oscillate: going compact
     raises --pdp-peek to 150px, which shrinks the image, which can un-trigger the
     condition, which restores the 75px peek… Predicting "would this image fill the
     window in the desktop layout?" is a stable question with one answer.

     COMPACT_PEEK_PX / IMG_HEIGHT_RATIO mirror --pdp-peek and .product-page-image's
     max-height in style-product.css — keep them in sync. */
  var PEEK_DESKTOP_PX = 75;
  var IMG_HEIGHT_RATIO = 0.70125;
  /* How much extra height the title may take before that counts as a wrap rather
     than measurement noise (see syncPeekHeight). Comfortably above the pixel or two
     of subpixel/metric noise, comfortably below the ~15px a real second row adds. */
  var PEEK_SNAP_TOL = 6;
  var COMPACT_MAX_WIDTH = 573; // the old media-query breakpoint, still a floor

  /* opts.force runs the switch during a row fade. Nothing else may: the measuring
     this does — peekWouldOverlap toggling body.pdp-compact, syncPeekHeight adding
     and removing the inherited --pdp-peek on <body> — invalidates computed style
     for the whole subtree, and landing that between the frame a fade sets its
     duration and the frame it changes opacity is what let the browser drop the
     transition and snap the row/title/price in (the same class of bug the price's
     "swap the curve a frame early" comment in fadeRowIn describes). The image
     decode handlers in refreshIndicator() call this, and on a SKU swap they fire
     right inside the 900ms fade-in — so they are the ones being held here. The
     swap's own forced pass runs in applyTitleEarly, while the row is out, and
     fadeRowIn re-runs the switch once the fade has finished. */
  function updateCompactPeek(opts) {
    if (rowFading && !(opts && opts.force)) return;

    /* PDP gone (the index swap closed and wiped #product-panel). The window
       listeners bound in init OUTLIVE the swap — and on mobile, iOS fires a
       resize whenever the URL bar collapses/expands during a plain scroll of
       the INDEX — while below 573px `compact` computes unconditionally true.
       Without this guard the first such resize after going home re-added
       body.pdp-compact to the page. (Historically that hid the FOOTER logo on
       the home grid; the footer-logo hider is now scoped to #pdp-swap /
       template-product in style-product.css, but stray pdp-compact would still
       drag --pdp-peek and the compact layout rules onto the index.)
       Self-clean instead — same pattern as syncPeekHeight's no-panel branch
       and updateDocked's header guard. */
    if (!panelEl || !panelEl.isConnected) {
      document.body.classList.remove("pdp-compact");
      return;
    }

    var compact = window.innerWidth <= COMPACT_MAX_WIDTH;

    if (!compact && scrollerEl) {
      var img = null;
      var imgs = scrollerEl.querySelectorAll(".product-page-image");
      for (var i = 0; i < imgs.length; i++) {
        var c = imgs[i].closest(".img-and-loading-container");
        if (c && c.style.display === "none") continue; // hidden variant set
        if (imgs[i].naturalWidth > 0) { img = imgs[i]; break; }
      }
      if (img) {
        var ratio = img.naturalWidth / img.naturalHeight;
        var height = IMG_HEIGHT_RATIO * (window.innerHeight - PEEK_DESKTOP_PX);
        compact = height * ratio >= window.innerWidth;
      }
    }

    /* Second trigger: the row itself has run out of width. The type title is
       white-space:nowrap and .product-panel-info is min-width:0, so once the two
       sides can't both fit, the title doesn't shrink or wrap — it overflows its box
       and runs UNDER the dropdown / add-to-cart. Stack instead, the moment they
       would touch. */
    if (!compact) compact = peekWouldOverlap();

    var flipped = document.body.classList.contains("pdp-compact") !== compact;
    document.body.classList.toggle("pdp-compact", compact);

    /* opts.decisionOnly: keep the compact decision current, but do NOT re-measure
       the height unless that decision actually flipped. Passed by the callers that
       fire while the product is ON SCREEN — the image-decode handlers and the
       post-fade cleanup. An image decoding cannot change the bar's height (no
       image is in the bar), so a re-measure there can only produce a late write of
       a value that should already be correct — and a late write while the row is
       visible IS the shift. Open, resize and the swap's own pass measure fully. */
    if (opts && opts.decisionOnly && !flipped) return;

    /* The compact bar is LOCKED to --pdp-peek, so re-measure whenever the switch
       runs — but only once the text is final, since that measurement is exactly
       what the fallback-font metrics get wrong. */
    if (typeof window.__whenTextReady === "function") window.__whenTextReady(syncPeekHeight);
    else syncPeekHeight();
  }

  /* ---------- desktop row-overflow trigger ----------
     Does the peek's left side (the EN/JP type title) still fit beside the actions
     on the right? Measured, not guessed: the title's width depends on the type
     name and the JP -jp._title metafield, neither of which a breakpoint knows.

     Measured in the DESKTOP layout unconditionally — the class is dropped for the
     read and restored before the task ends, so nothing paints in between (the same
     trick syncPeekHeight uses). That matters for the same reason the image
     prediction above sticks to the desktop peek constant: reading the live compact
     box would ask a question whose answer depends on the state it is about to set
     (compact lets the JP title wrap, which makes it narrower, which un-triggers
     compact, which makes it wide again…). Asking "would these two collide in the
     desktop row?" has one stable answer.

     The title row is the trigger, not the family SKU row beside it: that row is
     overflow-x:auto and is DESIGNED to scroll once squeezed (see
     .product-panel-family), so its content width overflowing is not a collision.
     The title, being nowrap on a min-width:0 parent, genuinely overlaps. */
  var overlapCacheKey = null;
  var overlapCacheValue = false;

  function peekWouldOverlap() {
    if (!panelEl || !panelEl.isConnected) return false;
    var peek = panelEl.querySelector(".product-panel-peek");
    var title = panelEl.querySelector(".product-panel-peek .product-title-container");
    var actions = panelEl.querySelector(".product-panel-actions");
    if (!peek || !title || !actions) return false;

    /* Only the window width and the title text can change this answer, and this
       runs on every image decode — so cache it. The measurement below drops and
       restores body.pdp-compact, which invalidates every computed style under it;
       doing that dozens of times per open (and inside fades) is exactly the churn
       that was disturbing the transitions. */
    var key = window.innerWidth + "|" + title.textContent + "|" + (window.__textReadyDone ? "1" : "0");
    if (key === overlapCacheKey) return overlapCacheValue;
    overlapCacheKey = key;

    var body = document.body;
    var wasCompact = body.classList.contains("pdp-compact");
    if (wasCompact) body.classList.remove("pdp-compact");

    var cs = getComputedStyle(peek);
    var available =
      peek.clientWidth -
      (parseFloat(cs.paddingLeft) || 0) -
      (parseFloat(cs.paddingRight) || 0);
    /* scrollWidth, not clientWidth: the title has already overflowed its squeezed
       box by the time this matters, and scrollWidth is what it WANTS. */
    var needed =
      title.scrollWidth + (parseFloat(cs.columnGap) || 0) + actions.offsetWidth;

    if (wasCompact) body.classList.add("pdp-compact");

    overlapCacheValue = available > 0 && needed >= available;
    return overlapCacheValue;
  }

  /* ---------- peek height reservation ----------
     In compact the peek's height is LOCKED to --pdp-peek (style-product.css)
     instead of sized by its content, because the image area reserves exactly
     calc(100svh - var(--pdp-peek)): if the bar's real height and that reservation
     ever disagree, the difference shows as dead space under the images or as the
     bar overlapping them.

     The CSS 150px is only the single-line default. The bar's true height depends
     on text Liquid can't measure — the EN type title (product.type) beside the
     JP -jp._title metafield, which wrap onto two lines at some widths, plus the
     family SKU row. So measure the natural height once the text is final and
     write it back: the lock and the reservation are then the same number by
     construction, and no later font/metafield reflow can move the bar.

     Measuring costs one forced layout (unlock to auto, read, restore) and runs
     only on open / resize / compact toggle / family swap. */
  /* A family swap patches the peek in several steps and across several tasks
     (title on fragment arrival, then actions/family/body once the fade-out has
     elapsed, then again as images decode). Measuring after each one wrote the
     height more than once, which is what read as the bar shifting up and down
     after a press. So the swap SUSPENDS routine measurement and takes exactly one
     forced measurement, as the LAST step of applySwapContent — by then the title,
     the family row and the actions (whose dropdown the incoming product may or may
     not have) are all patched, so what it measures is the final bar.
     A later resize / compact toggle re-measures as usual.

     Set window.__PDP_DEBUG = true in the console before pressing a SKU to log every
     write with its value and the call path. One line per swap is correct; two or
     more IS the shifting, and the trace says which caller to chase. */
  var peekSyncSuspended = false;
  var peekHeightWritten = null; // last value written to --pdp-peek, or null if unset

  function syncPeekHeight(opts) {
    var force = opts && opts.force;
    if ((peekSyncSuspended || rowFading) && !force) return;
    // No panel (swap closed, or a deferred call landing after it): drop the
    // measured override so a reopen starts from the stylesheet's own value.
    if (!panelEl || !panelEl.isConnected) {
      peekHeightWritten = null;
      document.body.style.removeProperty("--pdp-peek");
      return;
    }
    var peek = panelEl.querySelector(".product-panel-peek");
    if (!peek) return;
    if (!document.body.classList.contains("pdp-compact")) {
      // desktop peek is already a fixed 75px strip — drop any measured override
      if (peekHeightWritten !== null) {
        peekHeightWritten = null;
        document.body.style.removeProperty("--pdp-peek");
      }
      return;
    }

    /* The bar's height is the stylesheet's own value PLUS whatever extra height
       the title takes by wrapping. Nothing else.

       Measuring the whole bar instead — which is what this used to do — meant the
       number carried every other difference between two products as well: EN with
       a JP title beside it vs EN alone, one font's line box vs another's, subpixel
       rounding. Two products whose titles both sit on ONE line could measure 152
       and 158, and the bar moved 6px between them for no reason anyone could see.
       That is the unnecessary shifting, and it was intermittent because it depended
       on which pair of products you happened to step between.

       So measure only the thing that is allowed to change the height: the title
       block, twice — as it lays out now, and forced onto a single row. The
       difference is the wrap's contribution and nothing else. Every unwrapped
       title, whatever it says, yields 0 and therefore the identical bar. */
    var title = peek.querySelector(".product-title-container");
    var baseline = peekBaseline();
    if (!title || !baseline) return;

    var wrappedH = title.getBoundingClientRect().height;

    /* Force the single-row reference: nowrap on the container (so EN and JP can't
       sit on separate rows) and on the JP itself (compact gives it
       white-space:normal so a long one can break inside itself — see
       style-product.css). Restored before anything can paint. */
    var jp = title.querySelector(".product-title-jp");
    var prevWrap = title.style.flexWrap;
    var prevWS = jp ? jp.style.whiteSpace : null;
    title.style.flexWrap = "nowrap";
    if (jp) jp.style.whiteSpace = "nowrap";
    var singleRowH = title.getBoundingClientRect().height;
    title.style.flexWrap = prevWrap;
    if (jp) jp.style.whiteSpace = prevWS;

    var extra = Math.round(wrappedH - singleRowH);
    if (extra <= PEEK_SNAP_TOL) extra = 0;   // sub-pixel noise, not a wrap

    /* null means "no override — the stylesheet holds the bar", which is the state
       every unwrapped title resolves to, whatever it says. Comparing against it
       directly is what makes a repeat call a true no-op: --pdp-peek is inherited
       from <body>, so even re-writing an identical value dirties the subtree, and
       these calls come thick and fast (every image decode, every resize tick). */
    var want = extra === 0 ? null : baseline + extra;
    if (want === peekHeightWritten) return;

    if (debugOn()) {
      traceLine("WRITE " + peekHeightWritten + " -> " + (want === null ? "stylesheet " + baseline : want) +
                " (wrap+" + extra + ")");
      if (typeof console !== "undefined") {
        console.log("[peek] stack", (new Error().stack || "").split("\n").slice(1, 5).join(" <- "));
      }
    }
    peekHeightWritten = want;
    if (want === null) document.body.style.removeProperty("--pdp-peek");
    else document.body.style.setProperty("--pdp-peek", want + "px");
  }

  /* The stylesheet's OWN --pdp-peek for the layout in play (138px compact, 150px
     at ≤573px). Read by clearing the inline override for one computed-style read,
     then putting it back — the one place that global invalidation is worth it, and
     it happens once per breakpoint per session, cached below. */
  var peekBaselineCache = {};

  function peekBaseline() {
    var key = window.innerWidth <= COMPACT_MAX_WIDTH ? "narrow" : "wide";
    if (peekBaselineCache[key] != null) return peekBaselineCache[key];
    var inline = document.body.style.getPropertyValue("--pdp-peek");
    if (inline) document.body.style.removeProperty("--pdp-peek");
    var v = parseFloat(getComputedStyle(document.body).getPropertyValue("--pdp-peek"));
    if (inline) document.body.style.setProperty("--pdp-peek", inline);
    if (isNaN(v)) return null;
    peekBaselineCache[key] = v;
    return v;
  }

  /* The panel header is position:sticky at top:50px; it "docks" against the nav
     strip once the panel's top scrolls up past the 50px nav line. Toggle
     body.pdp-docked so the nav can snap to the panel's background. Keyed off the
     PANEL top (not the sticky header's measured top, which sits at exactly 50px
     when stuck and is subpixel-fragile against a `<= 50` test). +1px tolerance
     covers rounding. Self-clears when the panel is gone (post swap-close). */
  var NAV_H = 50;
  function updateDocked() {
    var panel = document.getElementById("product-panel");
    if (!panel) {
      document.body.classList.remove("pdp-docked");
      return;
    }
    document.body.classList.toggle(
      "pdp-docked",
      panel.getBoundingClientRect().top <= NAV_H + 1
    );
  }

  /* Runs on the hard PDP (auto) and is also called by the index body-swap via
     window.__pdpInit after it injects the panel. Idempotent: queries fresh
     elements each call, binds window-level listeners only once. */
  function initPanel() {
    panelEl = document.getElementById("product-panel");
    if (!panelEl) return;

    indicatorIntroDone = false; // fresh open: let the first fill measurement grow in
    /* Seed the page-top edge detector from where the page ACTUALLY is, so an open
       from a scrolled position doesn't count its scroll-restore as a crossing. */
    pageWasAwayFromTop =
      (window.pageYOffset || document.documentElement.scrollTop || 0) > PAGE_TOP_SLOP;

    scrollerEl = document.getElementById("product-images");
    fillEl = panelEl.querySelector(".product-scroll-indicator-fill");
    indicatorEl = panelEl.querySelector(".product-scroll-indicator");
    if (scrollerEl && fillEl) {
      scrollerEl.addEventListener("scroll", updateIndicator, { passive: true });
      scrollerEl.addEventListener("scroll", nudgeIndicator, { passive: true });
      bindGestureNudge();
      refreshIndicator();
      setupDragScroll();
      /* The image heights are svh-relative, so a viewport change reflows the row
         and shifts scrollWidth. The one-shot resize/load measurements can land
         before that reflow settles, leaving a stale fill until the next scroll.
         A ResizeObserver on the scroller fires on every relayout, so the fill
         always reflects the real scroll distance. Rebound per open (fresh el). */
      if (typeof ResizeObserver !== "undefined") {
        if (sizeObserver) sizeObserver.disconnect();
        sizeObserver = new ResizeObserver(function () { updateIndicator(); });
        sizeObserver.observe(scrollerEl);
      }
    }

    loadVariantData();
    initVariantSelect();
    initFamily();
    setupPeekFamilyScroll();
    bindInstructionVideos();
    loadCart();

    // Sync the docked-nav background to the current scroll on (re)open — the swap
    // reuses this init for each product, and the page starts at the top (not docked).
    updateDocked();
    updateCompactPeek();
    /* Re-decide once the webfonts are in: the overlap test above measures the
       title, and until the real face lands that width is the fallback's. Cheap and
       synchronous on every open after the first (see window.__textReady). */
    if (typeof window.__whenTextReady === "function") {
      window.__whenTextReady(updateCompactPeek);
    }

    if (!windowListenersBound) {
      windowListenersBound = true;
      window.addEventListener("resize", updateIndicator);
      window.addEventListener("resize", updateCompactPeek);
      /* The info panel's sticky header docks under the 50px nav strip; toggle
         body.pdp-docked the instant it sticks so the nav gains the panel's
         background (see style-product.css). Bound once; guards on the header
         being present so it self-clears after a swap close. */
      window.addEventListener("scroll", updateDocked, { passive: true });
      window.addEventListener("scroll", nudgeIndicatorAtPageTop, { passive: true });
      /* recompute the button when the cart changes (e.g. adding the last unit
         flips ADD TO CART → IN CART) */
      document.addEventListener("cart:updated", function (e) {
        if (e.detail) currentCart = e.detail;
        /* Reconcile rather than clear. Wiping pendingAdds on ANY cart:updated
           dropped holds the incoming cart did not account for — a remove (or a
           bare refresh) landing while an add was in flight unlocked the button
           for a unit that was already spoken for. reconcilePending only retires
           a hold once the authoritative cart actually shows that unit. */
        reconcilePending();
        reassertButton();
      });

      /* The cart-close safety net in script-cart.js also writes `disabled`. */
      document.addEventListener("cart:closed", reassertButton);

      /* Lock on the CLICK, not on the cart response. Counting the submitted unit
         immediately means the button reads IN CART / SOLD OUT the moment it is
         pressed. Capture phase, and separate from script-cart.js's own submit
         handler — that one runs first (it registers first) and starts the fetch;
         this one only updates our state. */
      document.addEventListener("submit", function (e) {
        var f = e.target;
        if (!f || f.id !== "add-to-cart-form") return;
        var idInput = f.querySelector('input[name="id"]');
        var id = idInput ? String(idInput.value) : null;
        if (!id) return;
        if (!pendingAdds[id]) pendingBase[id] = cartOnlyQtyFor(id);
        pendingAdds[id] = (pendingAdds[id] || 0) + 1;
        applyButtonState();
      }, true);

      /* Hard stop. `disabled` is written by two files and a stray re-enable would
         otherwise be an oversell; refusing the click itself cannot be raced —
         preventDefault here means the form never submits at all. */
      document.addEventListener("click", function (e) {
        if (buttonState === "add") return;
        if (!e.target || !e.target.closest) return;
        var btn = e.target.closest("#add-to-cart-form button");
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        btn.disabled = true;
      }, true);
      /* On the hard PDP this script owns history (family swaps). In the index
         body-swap the index owns popstate, so don't double-handle there. */
      if (document.body.classList.contains("template-product")) {
        window.addEventListener("popstate", onPopState);
      }
    }
  }

  /* Mobile: a horizontal swipe ANYWHERE in the bottom bar scrolls the SKU
     (family) row. Native touch scrolling only responds to gestures that start on
     the scrollable element itself, and in the compact layout that row is a thin
     strip in the top half of the bar — so a swipe across the button area, the
     type title, or the gap between them did nothing. This forwards the delta.

     Bound per header element (dataset flag): the index body-swap injects a fresh
     #product-panel on every open, and initPanel re-runs there.

     Deliberately does nothing when the row isn't overflowing — measured per
     gesture, since a family swap changes the SKU list. */
  function setupPeekFamilyScroll() {
    if (!panelEl) return;
    var header = panelEl.querySelector(".product-panel-header");
    if (!header || header.dataset.famScrollBound === "1") return;
    header.dataset.famScrollBound = "1";

    var AXIS_LOCK = 6;   // px of travel before committing the gesture to an axis
    var fam = null, axis = null, startX = 0, startY = 0, startScroll = 0;

    header.addEventListener("touchstart", function (e) {
      fam = null;
      axis = null;
      if (e.touches.length !== 1) return;
      if (!document.body.classList.contains("pdp-compact")) return;
      /* started on the row itself → let the browser scroll it natively */
      if (e.target.closest && e.target.closest(".product-panel-family")) return;
      var f = panelEl.querySelector(".product-panel-family");
      if (!f || f.scrollWidth <= f.clientWidth + 1) return;   // nothing to scroll
      fam = f;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startScroll = f.scrollLeft;
    }, { passive: true });

    /* non-passive: a horizontal gesture has to be preventDefault'd or iOS starts
       scrolling the page with it. The axis lock keeps vertical swipes untouched,
       so scrolling the page from the bar still works. */
    header.addEventListener("touchmove", function (e) {
      if (!fam || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
      if (axis !== "x") return;
      e.preventDefault();
      fam.scrollLeft = startScroll - dx;
    }, { passive: false });

    function endSwipe() { fam = null; axis = null; }
    header.addEventListener("touchend", endSwipe);
    header.addEventListener("touchcancel", endSwipe);
  }

  /* click-and-drag to scroll the image row horizontally (mouse/pen; touch already
     pans natively) + click-to-advance. Bound once per scroller element via a
     dataset flag. */
  function setupDragScroll() {
    if (!scrollerEl || scrollerEl.dataset.dragBound === "1") return;
    scrollerEl.dataset.dragBound = "1";

    var down = false, startX = 0, startScroll = 0, moved = false;

    scrollerEl.addEventListener("pointerdown", function (e) {
      moved = false; // reset before the touch bail-out so a pan can't inherit a stale drag
      cancelStepScroll(); // a touch/drag takes the row back off any running step
      if (e.pointerType === "touch") return;
      down = true;
      startX = e.clientX;
      startScroll = scrollerEl.scrollLeft;
      scrollerEl.setPointerCapture(e.pointerId);
      scrollerEl.classList.add("dragging");
    });
    scrollerEl.addEventListener("pointermove", function (e) {
      if (!down) return;
      if (Math.abs(e.clientX - startX) > DRAG_SLOP) moved = true;
      scrollerEl.scrollLeft = startScroll - (e.clientX - startX);
      nudgeIndicator(); // dragging against either end moves nothing → no scroll event
    });
    function end() {
      down = false;
      scrollerEl.classList.remove("dragging");
    }
    scrollerEl.addEventListener("pointerup", end);
    scrollerEl.addEventListener("pointercancel", end);
    scrollerEl.addEventListener("dragstart", function (e) { e.preventDefault(); });

    /* Click the right ADVANCE_ZONE of the image area → next image; the remaining
       left slice → previous image. A drag ends in a click event too, so anything
       that moved more than DRAG_SLOP is treated as a scroll, not a tap. */
    scrollerEl.addEventListener("click", function (e) {
      if (moved) { moved = false; return; }
      var rect = scrollerEl.getBoundingClientRect();
      if (rect.width <= 0) return;
      stepImage((e.clientX - rect.left) / rect.width < 1 - ADVANCE_ZONE ? -1 : 1);
    });
  }

  /* ---------- click-to-step ---------- */

  /* Scroll so the next/previous image leads the row.
       dir  1 → the first shown container whose left edge sits past the current
              scroll position; past the last one it wraps back to the start.
       dir -1 → the last shown container whose left edge sits before it; at the
              start it wraps to the end of the row.
     Positions are measured against the scroller's own rect rather than offsetLeft
     — the containers' offsetParent is not guaranteed to be the scroller (it is
     position:static). */
  function stepImage(dir) {
    if (!scrollerEl || rowFading) return; // a content fade owns the row until it lands
    var max = scrollerEl.scrollWidth - scrollerEl.clientWidth;
    if (max <= 1) return; // everything already fits — nothing to step through

    var left = scrollerEl.getBoundingClientRect().left;
    var live = scrollerEl.scrollLeft;
    /* Step from where the row is HEADED, not where it currently is. Clicking twice
       in a row used to measure against the mid-animation position, so the second
       click frequently resolved to the same image the first was already travelling
       to — the click did nothing, or re-eased from a moving start (the jitter).
       Against the pending destination, two clicks reliably advance two images. */
    var base = stepPendingTarget === null ? live : stepPendingTarget;
    /* no candidate in that direction → wrap: forward to the first image, back to
       the far end of the row (the last image's resting position) */
    var target = dir > 0 ? 0 : max;
    var found = false;

    var containers = scrollerEl.querySelectorAll(".img-and-loading-container");
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      if (c.style.display === "none") continue; // hidden variant set
      /* absolute offset within the row: rects are viewport-relative, so add the
         LIVE scroll to make the value independent of where the row sits now */
      var pos = live + (c.getBoundingClientRect().left - left);
      if (dir > 0) {
        if (pos > base + 1) { target = pos; found = true; break; }
      } else if (pos < base - 1) {
        target = pos; // keep the LAST one before base, so don't break
        found = true;
      }
    }

    target = Math.max(0, Math.min(target, max));
    /* A candidate can exist but be unreachable — at the end of the row the last
       image's lead position sits past `max`, so clamping lands back on where we
       already are. That's the real end of the row: wrap, don't no-op. */
    if (found && Math.abs(target - base) < 1) {
      found = false;
      target = dir > 0 ? 0 : max;
    }

    if (!found) { wrapScroll(target); return; } // ran off the end → fade around
    stepScrollTo(target);
  }

  /* MOBILE ONLY. The native smooth scroll has no specified duration, and iOS
     Safari's is slow enough that a step crawls on an iPhone. Desktop keeps the
     native scroll — it already reads correctly there, so there is no reason to
     hand-animate it. Gated on a coarse pointer rather than a user-agent sniff. */
  /* Cancels the animation AND forgets where it was headed — used when the user
     grabs the row themselves, or when a wrap takes it over. */
  function cancelStepScroll() {
    if (stepAnimFrame !== null) {
      cancelAnimationFrame(stepAnimFrame);
      stepAnimFrame = null;
    }
    if (stepPendingTimer) {
      clearTimeout(stepPendingTimer);
      stepPendingTimer = null;
    }
    stepPendingTarget = null;
  }

  function isTouchPointer() {
    return !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  }

  function stepScrollTo(target) {
    if (!scrollerEl) return;
    cancelStepScroll();
    stepPendingTarget = target; // stepImage measures its next step from here

    if (!isTouchPointer()) {
      scrollerEl.scrollTo({ left: target, behavior: "smooth" });
      /* The native scroll reports no completion, so release the pending target on
         a timer generous enough to cover it; a further click before then simply
         retargets, which is the behaviour we want anyway. */
      stepPendingTimer = setTimeout(function () {
        stepPendingTimer = null;
        stepPendingTarget = null;
      }, 700);
      return;
    }

    var el = scrollerEl;
    var from = el.scrollLeft;
    var delta = target - from;
    if (!delta) {
      stepPendingTarget = null;
      return;
    }

    var startTs = null;
    var frame = function (ts) {
      if (startTs === null) startTs = ts;
      var t = Math.min(1, (ts - startTs) / STEP_SCROLL_MS);
      // ease-in-out cubic — accelerates off the mark, settles into the target
      var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      el.scrollLeft = from + delta * e;
      if (t < 1) {
        stepAnimFrame = requestAnimationFrame(frame);
      } else {
        stepAnimFrame = null;
        stepPendingTarget = null; // landed — measure from the real position again
      }
    };
    stepAnimFrame = requestAnimationFrame(frame);
  }

  /* ---------- fade the image row across a content change ----------

     Every change of what the row shows — wrapping past either end, switching
     variant, swapping to a sibling SKU — hides the row, changes it while it can't
     be seen, and brings it back. Timing is the logo→grid PDP exit
     (closeProductSwap's 0.10s ease-out, then pmFadeInView's 0.9s ease-in-out).

     `apply` runs once the row is invisible; the caller decides when to call
     fadeRowIn (a variant switch can do it immediately, a SKU swap has to wait for
     the incoming images to decode). Callers MUST reach fadeRowIn on every path or
     the row stays hidden.

     opts.indicator additionally fades the indicator TRACK — used by the wrap,
     where the fill would otherwise visibly rewind the whole bar. Fading the track
     (not the fill) takes the fill with it, so the indicator goes and returns
     whole. The switchers leave the indicator alone: it just moves with the row.
     nudgeIndicator bails while wrapFading, so a scroll event mid-fade can't snap
     the track back to opacity 1. */
  /* The row's own fade runs on the Web Animations API rather than a CSS
     transition. A transition only plays if the browser resolved the pre-change
     style first, which leaves it at the mercy of anything else that touched style
     in the same frame — a forced layout from a measurement, a class toggle, a
     freshly injected subtree. Pressing a SKU has to dissolve EVERY time, so the
     keyframes are explicit (from → to, no inference from computed state) and the
     animation object is owned, so a new fade cancels its predecessor cleanly
     instead of racing it. */
  var rowAnim = null;

  function animateRowOpacity(el, from, to, ms, easing) {
    if (rowAnim) { rowAnim.cancel(); rowAnim = null; }
    el.style.transition = "";                        // the animation owns opacity
    el.style.opacity = to === 1 ? "" : String(to);   // resting state once it ends
    if (!el.animate) return;                         // no WAAPI: state above stands
    var anim = el.animate(
      [{ opacity: String(from) }, { opacity: String(to) }],
      { duration: ms, easing: easing }
    );
    rowAnim = anim;
    anim.onfinish = function () { if (rowAnim === anim) rowAnim = null; };
  }

  function fadeRowOut(apply, opts) {
    opts = opts || {};
    if (!scrollerEl) {
      if (apply) apply();
      return;
    }
    var el = scrollerEl;
    /* Kill the previous fade's cleanup. It clears the row's inline transition and
       opacity, and if it fires after a NEW fade has started — press two sibling
       SKUs inside 900ms, or switch variant right after a swap — it wipes the new
       fade's inline styles and the row snaps to visible with no animation. That is
       the "sometimes it just switches" case; it has nothing to do with which
       product you pick, only with how soon you pick the next one. */
    if (rowFadeTimer) { clearTimeout(rowFadeTimer); rowFadeTimer = null; }
    rowFading = true;

    /* opts.price fades the add-to-cart price along with the row, so the number
       doesn't snap to the new variant/SKU while the images it belongs to are still
       dissolving. Driven by a body class rather than an element reference: the SKU
       swap REPLACES .product-panel-actions wholesale, so any element we grabbed
       here would be stale by the time we faded it back in. The class also means a
       freshly injected price starts at opacity 0 and rides the same fade-in.
       Cleared unconditionally in fadeRowIn, so no path can strand it hidden. */
    if (opts.price) {
      // only opacity changes here — the 100ms fade-out curve is the element's base
      document.body.classList.remove("pdp-price-in");
      document.body.classList.add("pdp-price-fading");
    }

    /* opts.title does the same for the peek's EN/JP type title. Separate from
       opts.price because the two don't always change together: a variant switch
       moves the price but not the title, so only the family SKU swap passes this
       and the title stays put on a colour change. Same classes-on-body reasoning,
       same curves — the pair fades in lockstep with the price. */
    if (opts.title) {
      document.body.classList.remove("pdp-title-in");
      document.body.classList.add("pdp-title-fading");
    }

    if (opts.indicator) {
      wrapFading = true;
      if (indicatorIdleTimer) clearTimeout(indicatorIdleTimer);
      if (indicatorEl) {
        indicatorEl.style.transition = "opacity " + WRAP_FADE_OUT_MS + "ms ease-out";
        indicatorEl.style.opacity = "0";
      }
    }
    /* Read where the row actually IS before cancelling anything, so a fade-out
       interrupting a fade-in picks up from the visible value instead of jumping. */
    var current = parseFloat(getComputedStyle(el).opacity);
    animateRowOpacity(el, isNaN(current) ? 1 : current, 0, WRAP_FADE_OUT_MS, "ease-out");

    setTimeout(function () {
      if (apply) apply();
    }, WRAP_FADE_OUT_MS);
  }

  function clearPriceFade() {
    document.body.classList.remove("pdp-price-fading");
    document.body.classList.remove("pdp-price-in");
    document.body.classList.remove("pdp-title-fading");
    document.body.classList.remove("pdp-title-in");
  }

  function fadeRowIn(opts) {
    opts = opts || {};
    if (rowFadeTimer) { clearTimeout(rowFadeTimer); rowFadeTimer = null; } // see fadeRowOut
    /* Safety net: every exit path reaches here, including the ones that bail out
       of applySwapContent early (panel closed, no image row in the fragment), so a
       frozen image area can never outlive the swap that froze it. A no-op on the
       normal path, where measureFinalBar already released it. */
    releaseImageArea();
    /* The swap is over (every exit path reaches here) — let routine measurement
       resume for resizes and compact toggles. Deliberately does NOT re-measure:
       re-reading here would be a SECOND height write for the same swap, which is
       the shifting this suspend removes. */
    peekSyncSuspended = false;
    if (!scrollerEl) {
      rowFading = false;
      wrapFading = false;
      clearPriceFade();
      return;
    }
    var el = scrollerEl;
    /* Swap the price to the slow curve NOW, a frame before its opacity changes
       below — changing duration and value together is what let the browser skip
       the transition and snap it (see style-product.css). */
    document.body.classList.add("pdp-price-in");
    /* Unconditional, exactly like the price above: revealSwappedImages calls
       fadeRowIn() with no opts, so gating this on a flag would strand the title
       hidden on the one path that needs it. On a fade that never touched the
       title it only swaps a duration on an element already at opacity 1 — no
       visual effect. */
    document.body.classList.add("pdp-title-in");
    /* The indicator still uses a CSS transition, so it keeps the "set the curve a
       frame early" treatment. The row doesn't need it — animateRowOpacity states
       both ends outright. */
    if (opts.indicator && indicatorEl) {
      indicatorEl.style.transition = "opacity " + WRAP_FADE_IN_MS + "ms ease-in-out";
    }
    /* Let the changed content paint at opacity 0 first, or the transition eases
       from the pre-change frame. */
    requestAnimationFrame(function () {
      /* Explicitly from 0: the row is coming back from invisible, so state it
         rather than trusting the computed value — a stale cleanup or an
         interrupted fade could otherwise leave it already at 1, and the dissolve
         would be skipped for that one press. */
      animateRowOpacity(el, 0, 1, WRAP_FADE_IN_MS, "ease-in-out");
      /* Force the browser to resolve the CURRENT (opacity 0) style of the two
         class-driven fades before un-fading them. A SKU swap REPLACES
         .product-panel-actions wholesale, so on this frame the price element may
         be brand new and never have had its opacity:0 computed — the browser then
         resolves once, sees 1 with a 900ms transition, and has nothing to
         interpolate from: the price/title snap in while the images dissolve. One
         read per element is enough, and this frame recalculates style anyway. */
      [".product-title-container", ".product-panel-price", ".add-to-cart-price"].forEach(
        function (sel) {
          var n = panelEl && panelEl.querySelector(sel);
          if (n) void getComputedStyle(n).opacity;
        }
      );
      document.body.classList.remove("pdp-price-fading");
      document.body.classList.remove("pdp-title-fading");
      if (opts.indicator && indicatorEl) {
        indicatorEl.style.opacity = "1";
      }
      rowFadeTimer = setTimeout(function () {
        rowFadeTimer = null;
        el.style.transition = "";
        el.style.opacity = "";
        rowFading = false;
        document.body.classList.remove("pdp-price-in"); // back to the fade-out curve
        document.body.classList.remove("pdp-title-in");
        /* The switch was held for the duration of the fade (see updateCompactPeek)
           — run it now the animation is over. decisionOnly: the product is fully
           visible at this point, so this must not write a height. The swap already
           measured the final bar; the only thing left worth catching here is the
           desktop compact prediction flipping on the new image's aspect ratio, and
           a flip re-measures on its own. */
        updateCompactPeek({ decisionOnly: true });
        if (opts.indicator) {
          if (indicatorEl) indicatorEl.style.transition = ""; // back to the CSS 0.6s idle fade
          wrapFading = false;
          nudgeIndicator(); // restart the idle countdown now the reveal has landed
        }
      }, WRAP_FADE_IN_MS);
    });
  }

  /* Wrapping past either end is a REAL scroll you simply don't see. */
  function wrapScroll(to) {
    if (!scrollerEl || rowFading) return;
    fadeRowOut(function () {
      cancelStepScroll();          // no half-finished step animation dragging us back
      scrollerEl.scrollLeft = to;  // the scroll handler moves the fill with it
      fadeRowIn({ indicator: true });
    }, { indicator: true });
  }

  /* ---------- variant data (JSON tag) ---------- */

  function loadVariantData() {
    var tag = document.getElementById("pdp-variants");
    variantData = tag ? safeParse(tag.textContent) : {};
  }

  function safeParse(text) {
    try {
      return JSON.parse(text) || {};
    } catch (e) {
      return {};
    }
  }

  /* ---------- horizontal-scroll indicator ---------- */

  /* Smooth fill = the fraction of total content whose right edge is in view:
     (scrollLeft + clientWidth) / scrollWidth. At scroll 0 this is "how much fits
     on screen" — one image filling the viewport starts at its segment; if a 2nd
     image is also visible it starts proportionally further — growing to 100% at
     the end. Recomputed on scroll AND resize. */
  function updateIndicator() {
    if (!fillEl || !scrollerEl) return;
    /* A variant set is still decoding: its images have no width yet, so
       scrollWidth is collapsed and any reading here is bogus (usually 100%).
       The scroll event from resetting scrollLeft and the ResizeObserver both
       land in this window — hold the previous fill until refreshIndicator's
       load handler clears the flag and repaints authoritatively. */
    if (indicatorMeasuring) return;
    var total = scrollerEl.scrollWidth;
    if (total <= 0) {
      fillEl.style.width = "0%";
      return;
    }
    var seen = scrollerEl.scrollLeft + scrollerEl.clientWidth;
    var pct = Math.max(0, Math.min(1, seen / total));
    fillEl.style.width = pct * 100 + "%";
  }

  /* First fill measurement after an open. The fill can only be set once the images
     decode (see refreshIndicator) — at master resolution that lands AFTER the 0.9s
     panel fade, so the fill would pop to its value while everything else has already
     eased in. Set the width instantly to its target, then fade the fill IN on the
     same curve as the rest — so it reveals in place at its correct size instead of
     growing from the left edge. Clear the transition afterward so live scroll
     tracking stays instant. Subsequent measurements (scroll/resize/variant switch)
     go straight through updateIndicator. */
  function revealIndicator() {
    if (indicatorIntroDone || !fillEl) {
      updateIndicator();
      nudgeIndicator();
      return;
    }
    indicatorIntroDone = true;
    fillEl.style.opacity = "0";
    updateIndicator();
    // Next frame: width is applied, now transition opacity to reveal in place.
    requestAnimationFrame(function () {
      if (!fillEl) return;
      fillEl.style.transition = "opacity 0.9s ease-in-out";
      fillEl.style.opacity = "1";
      setTimeout(function () {
        if (fillEl) fillEl.style.transition = "";
        // Only now that the initial fade-in has finished do we start the
        // idle countdown — so the auto-hide can't begin mid-reveal.
        nudgeIndicator();
      }, 900);
    });
  }

  /* Auto-hide: show the indicator now, then fade the whole track out after
     INDICATOR_IDLE_MS of no scroll. Called on every scroll (fades it back in +
     resets the countdown) and once the fill first reveals (starts the countdown).
     Opacity lives on the track element, independent of the fill's own reveal
     transition, so the two never fight. */
  /* Coming back UP to the top of the page re-shows the indicator. The image row
     is the first screen, so returning to it is the same "where am I in this row?"
     moment as touching the row itself — but no horizontal scroll happened, so
     nothing above fires. Edge-triggered on the crossing (not on every scroll
     event at the top) so iOS rubber-banding at 0 can't hold the track open
     indefinitely; PAGE_TOP_SLOP absorbs the sub-pixel resting values some
     browsers report instead of a clean 0. */
  var PAGE_TOP_SLOP = 2;
  function nudgeIndicatorAtPageTop() {
    /* Bound once on window, so it outlives a swap close — after which indicatorEl
       points at a detached track. Scrolling the HOME grid back to top must not
       poke it. */
    if (!indicatorEl || !indicatorEl.isConnected) return;
    var atTop = (window.pageYOffset || document.documentElement.scrollTop || 0) <= PAGE_TOP_SLOP;
    if (atTop && pageWasAwayFromTop) nudgeIndicator();
    pageWasAwayFromTop = !atTop;
  }

  /* A scroll event only fires when scrollLeft actually CHANGES, so a gesture at
     either end of the row (swiping past the last image, dragging back at the
     first) moves nothing and the indicator stays hidden — even though the user
     is plainly asking "where am I?". These listeners nudge on the INPUT rather
     than on the resulting scroll, so an over-scrolled gesture brings the track
     back too. Bound once per scroller element (same dataset-flag pattern as the
     drag binding); real scrolls still nudge via the scroll listener above. */
  function bindGestureNudge() {
    if (!scrollerEl || scrollerEl.dataset.nudgeBound === "1") return;
    scrollerEl.dataset.nudgeBound = "1";
    /* Horizontal intent only — a vertical wheel/swipe over the image row is the
       user scrolling the PAGE, and shouldn't summon the row's indicator. */
    scrollerEl.addEventListener("wheel", function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) nudgeIndicator();
    }, { passive: true });

    var touchX = 0, touchY = 0;
    scrollerEl.addEventListener("touchstart", function (e) {
      if (!e.touches || !e.touches.length) return;
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    }, { passive: true });
    scrollerEl.addEventListener("touchmove", function (e) {
      if (!e.touches || !e.touches.length) return;
      var dx = Math.abs(e.touches[0].clientX - touchX);
      var dy = Math.abs(e.touches[0].clientY - touchY);
      if (dx > dy) nudgeIndicator();
    }, { passive: true });
  }

  function nudgeIndicator() {
    if (!indicatorEl || wrapFading) return; // a wrap owns the track's opacity
    indicatorEl.style.opacity = "1";
    if (indicatorIdleTimer) clearTimeout(indicatorIdleTimer);
    indicatorIdleTimer = setTimeout(function () {
      if (indicatorEl) indicatorEl.style.opacity = "0";
    }, INDICATOR_IDLE_MS);
  }

  /* Image widths (→ scrollWidth) aren't known until each image decodes. Updating
     before then gives a wrong (often 100%) value that then snaps back — the
     "flash". So only update once every VISIBLE image has loaded; until then leave
     the previous fill in place. */
  function refreshIndicator(opts) {
    opts = opts || {};
    if (!scrollerEl || !fillEl) return;

    var imgs = [];
    scrollerEl.querySelectorAll(".product-page-image").forEach(function (img) {
      var c = img.closest(".img-and-loading-container");
      if (c && c.style.display === "none") return; // hidden variant set
      imgs.push(img);
    });

    var pending = 0;
    imgs.forEach(function (img) {
      if (!img.complete) pending++;
    });

    if (pending === 0) {
      indicatorMeasuring = false;
      revealIndicator();
      // decisionOnly: image state can't change the bar's height — see updateCompactPeek
      updateCompactPeek({ decisionOnly: true });
      return;
    }

    /* Variant switch: the new set appears as soon as its images decode, one by one
       — waiting for the LAST one before showing the fill is what made the indicator
       arrive long after the images. Reveal now against the current measurement
       (undecoded slots still hold their .is-loading box, so it isn't nonsense) and
       refine it as each image lands. The initial open keeps the strict wait below:
       there, nothing is on screen yet, so an early bogus reading has nothing to be
       in sync with and only reads as a flash. */
    if (opts.immediate) {
      indicatorMeasuring = false;
      revealIndicator();
      imgs.forEach(function (img) {
        if (img.complete) return;
        img.addEventListener("load", updateIndicator, { once: true });
        img.addEventListener("error", updateIndicator, { once: true });
      });
      // this set's images may have a different aspect ratio (decision only —
      // the height was measured with the final bar, see updateCompactPeek)
      updateCompactPeek({ decisionOnly: true });
      return;
    }

    /* Hold the fill while the shown set decodes — updateIndicator (fired by the
       scrollLeft reset and the ResizeObserver) bails out until this clears. */
    indicatorMeasuring = true;

    imgs.forEach(function (img) {
      if (img.complete) return;
      var done = function () {
        pending--;
        // first decode gives us the aspect ratio the compact switch needs
        // (decision only — a decode can't change the bar's height)
        updateCompactPeek({ decisionOnly: true });
        if (pending === 0) {
          indicatorMeasuring = false;
          revealIndicator();
        }
      };
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  }

  /* Hidden image sets are emitted loading="lazy" so only the shown set decodes on
     open. When a set becomes visible (variant switch), promote its images to eager
     so they all load and the scroll indicator can measure the full scrollWidth. */
  function eagerLoad(container) {
    container.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.loading = "eager";
    });
  }

  /* Show only the selected variant's image set; fall back to the base set when
     that variant has none.

     `animate` fades the row across the change (see fadeRowOut). It is false for the
     initial wiring in initVariantSelect — the set being "switched" to is already the
     one on screen, and fading there would fight the PDP's own open fade. Only a
     real selection change animates. The indicator is NOT part of the fade: it just
     moves to the new start, per the same rule as before.

     `onHidden` runs in the same invisible window as the image swap — the caller
     puts anything whose CHANGE should be masked in there (the price figure). Text
     written before this call would visibly change and only then fade out. */
  function showVariantImages(variantId, animate, onHidden) {
    if (!scrollerEl) return;

    var swap = function () {
      if (onHidden) onHidden();
      var base = scrollerEl.querySelectorAll(
        '.img-and-loading-container[data-image-base="true"]'
      );
      var allVariant = scrollerEl.querySelectorAll(
        ".img-and-loading-container[data-variant-id]"
      );
      var selected = scrollerEl.querySelectorAll(
        '.img-and-loading-container[data-variant-id="' + variantId + '"]'
      );

      if (selected.length > 0) {
        base.forEach(function (c) { c.style.display = "none"; });
        allVariant.forEach(function (c) { c.style.display = "none"; });
        selected.forEach(function (c) { c.style.display = "block"; eagerLoad(c); });
      } else {
        allVariant.forEach(function (c) { c.style.display = "none"; });
        base.forEach(function (c) { c.style.display = "block"; eagerLoad(c); });
      }

      scrollerEl.scrollLeft = 0;
      if (indicatorSwitchTimer) clearTimeout(indicatorSwitchTimer);
      updateIndicator();
      refreshIndicator({ immediate: true });
    };

    if (!animate || rowFading) {
      swap();
      return;
    }
    fadeRowOut(function () {
      swap();
      fadeRowIn();
    }, { price: true });
  }

  /* ---------- variant selection ---------- */

  function findOptionByTitle(select, title) {
    var t = (title || "").trim().toLowerCase();
    if (!t) return null;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].textContent.trim().toLowerCase() === t) {
        return select.options[i].value;
      }
    }
    return null;
  }

  /* ---------- add-to-cart button state (sold-out / inventory / in-cart) ----------

     Resolves the button for the currently selected variant. Works for products
     with and without variants.

     The INVENTORY TRACKER OUTRANKS the manual sold-out metafield: for a variant
     that is inventory-tracked with policy "deny", the counted stock is the only
     authority, in both directions — stock left means ADD TO CART even if the
     metafield says sold out, and no stock left means the button locks even if
     the metafield is clear. The metafield is only consulted for variants the
     tracker can't speak for (untracked, or policy "continue").

       tracked + "deny":
         • qty − already-in-cart > 0                 → ADD TO CART
         • ≤ 0 and some of it is in YOUR cart        → IN CART   (disabled)
         • ≤ 0 and none of it is in your cart        → SOLD OUT  (disabled)
       otherwise:
         • manual sold-out metafield, or `available` false → SOLD OUT (disabled)
         • else → ADD TO CART

     NOTE: the disabled flag set here is re-asserted by script-cart.js, which
     re-enables the button after an add and on cart close — it skips any button
     whose .sold-out-text / .in-cart-text is showing, so those two states survive
     the add that created them. Keep those two <p> hooks in the button markup. */
  /* Units of `id` the authoritative cart says we hold — no optimism. Matches on
     `id` (what /cart.js calls the variant) and falls back to `variant_id` so the
     server-rendered window.__initialCart shape is read the same way. */
  function cartOnlyQtyFor(id) {
    if (!currentCart || !currentCart.items) return 0;
    var n = Number(id);
    var q = 0;
    currentCart.items.forEach(function (it) {
      var vid = Number(it.id != null ? it.id : it.variant_id);
      if (vid === n) q += it.quantity;
    });
    return q;
  }

  function cartQtyFor(id) {
    /* Units submitted on this page but not yet reflected in currentCart count as
       held. Without this the button stays ADD TO CART for the whole round trip of
       /cart/add.js + /cart.js — a window in which a second click adds a second
       unit of a one-off. The hold is retired by reconcilePending (the unit showed
       up in the real cart) or by window.__pdpAddSettled (the request settled). */
    return (pendingAdds[String(id)] || 0) + cartOnlyQtyFor(id);
  }

  /* Retire the optimistic holds the incoming cart now accounts for, and ONLY
     those. `landed` is how much the real quantity grew since each hold was taken,
     so a cart that shrank (a remove broadcast mid-add) leaves the hold standing
     instead of unlocking a unit that is still in flight. */
  function reconcilePending() {
    Object.keys(pendingAdds).forEach(function (id) {
      var landed = cartOnlyQtyFor(id) - (pendingBase[id] || 0);
      if (landed <= 0) return;
      pendingAdds[id] -= landed;
      pendingBase[id] += landed;
      if (pendingAdds[id] <= 0) {
        delete pendingAdds[id];
        delete pendingBase[id];
      }
    });
  }

  function releasePending(id) {
    id = String(id);
    if (!pendingAdds[id]) return;
    pendingAdds[id] -= 1;
    if (pendingAdds[id] <= 0) {
      delete pendingAdds[id];
      delete pendingBase[id];
    }
  }

  function loadCart() {
    /* Seed from the server-rendered cart so the FIRST paint of the button is
       already right. Without this there is a /cart.js round trip during which a
       variant the shopper already holds entirely reads as ADD TO CART — and a
       click landing in that window is an add the server has to refuse. */
    if (!currentCart && window.__initialCart) {
      currentCart = window.__initialCart;
      applyButtonState();
    }
    fetch("/cart.js", { credentials: "same-origin", cache: "no-store" })
      .then(function (r) { return r.json(); })
      .then(function (c) { currentCart = c; applyButtonState(); })
      .catch(function () {});
  }

  /* Re-assert `disabled` after the other writer (script-cart.js) has had its go.
     Two timers, not one: the rAF is the normal path, but a phone that is
     backgrounded mid-add (the shopper switches apps while /cart.js is in flight)
     stops firing rAF while the promise chain that clears the lock keeps running —
     so the setTimeout is what closes the gap there. applyButtonState is a pure
     re-derivation, so running it twice costs nothing. */
  function reassertButton() {
    applyButtonState();
    setTimeout(applyButtonState, 0);
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(applyButtonState);
  }

  function applyButtonState() {
    if (!currentVariantId) return;
    var v = variantData[currentVariantId];
    if (!v) return;

    var form = document.getElementById("add-to-cart-form");
    var button = form ? form.querySelector("button") : null;
    if (!button) return;
    /* Scoped to the form, not the document: these are the exact nodes
       script-cart.js reads back through isAddButtonLocked(form), so both files
       have to be looking at the same button for the lock contract to hold. */
    var addText = form.querySelector(".add-to-cart-text");
    var soldText = form.querySelector(".sold-out-text");
    var inCartText = form.querySelector(".in-cart-text");

    var state = "add";
    var manualSold = v.soldOut && v.soldOut !== "false";
    if (v.tracked && v.policy === "deny") {
      /* Counted stock wins outright — see the note above. */
      var held = cartQtyFor(currentVariantId);
      if (((v.qty || 0) - held) > 0) state = "add";
      else state = held > 0 ? "incart" : "sold";
    } else if (manualSold || v.available === false) {
      /* A server-REFUSED add (window.__pdpAddRejected) means "nothing left
         beyond what you already hold" — which is IN CART, not SOLD OUT, when the
         shopper is the one holding it. The manual metafield keeps its existing
         meaning: sold out is sold out whatever is in the cart. */
      state =
        v.rejected && cartQtyFor(currentVariantId) > 0 ? "incart" : "sold";
    }

    buttonState = state;
    button.disabled = state !== "add";
    if (addText) addText.style.display = state === "add" ? "block" : "none";
    if (soldText) soldText.style.display = state === "sold" ? "block" : "none";
    if (inCartText) inCartText.style.display = state === "incart" ? "block" : "none";
  }

  /* (Re)wire the variant <select> against the current variantData. Safe to call
     again after a swap — it queries fresh elements each time. */
  function initVariantSelect() {
    var select = document.getElementById("variant-selector");
    if (!select) {
      /* single-variant: no dropdown. Record the only variant as current (for the
         button state) and its title (so a later swap can match the colour). */
      var ids = Object.keys(variantData);
      if (ids.length) {
        currentVariantId = ids[0];
        lastVariantTitle = variantData[ids[0]].title;
      }
      applyButtonState();
      return;
    }

    var priceEl = panelEl.querySelector(".product-panel-price");
    var btnPriceEl = panelEl.querySelector(".add-to-cart-price");
    var selectedEl = panelEl.querySelector(".selected-variant");
    var form = document.getElementById("add-to-cart-form");
    var hiddenId = form ? form.querySelector('input[name="id"]') : null;

    /* Called with the change Event on a real selection, and with nothing for the
       initial wiring below — that argument is what decides whether the image row
       fades across the change or is simply set up in place. */
    function update(e) {
      var id = select.value;
      var v = variantData[id];
      if (!v) return;
      currentVariantId = id;

      /* The variant label and the form id are the user's own selection — they land
         immediately. The PRICE is written behind the fade instead (see below), so
         the figure doesn't change in plain sight and then dissolve. */
      if (selectedEl) selectedEl.textContent = v.title;
      if (hiddenId) hiddenId.value = id;
      lastVariantTitle = v.title;

      if (v.sku) {
        history.replaceState(
          Object.assign({}, history.state, { sku: v.sku }),
          "",
          window.location.pathname + window.location.search + "#" + v.sku
        );
      }

      applyButtonState();
      showVariantImages(id, !!e, function () {
        /* SKU title stays fixed (main product SKU); price tracks the variant. When
           animating, this runs while the row is at opacity 0 — the price fades out
           on the old figure and back in on the new one. */
        if (priceEl) priceEl.textContent = v.price;
        if (btnPriceEl) btnPriceEl.textContent = v.price;
      });
    }

    /* Preselect: 1) preserved variant title (family switch keeps the colour),
       2) a SKU hash deep link, else the default first option. */
    var matched = findOptionByTitle(select, lastVariantTitle);
    if (matched != null) {
      select.value = matched;
    } else {
      var hash = window.location.hash.substring(1);
      if (hash) {
        var id = Object.keys(variantData).find(function (k) {
          return variantData[k].sku === hash;
        });
        if (id) select.value = id;
      }
    }

    update();
    select.addEventListener("change", update);
  }

  /* ---------- collection family: prefetch + soft swap ---------- */

  function initFamily() {
    var fam = panelEl.querySelector(".product-panel-family");
    if (!fam) return;

    /* The product you opened (is-current) always leads the flex row. product-body
       .liquid already renders it first, so this is normally a no-op — it stays as
       the fallback for a fragment whose current SKU sits mid-row (and it must NOT
       be the primary mechanism: reordering after first paint is a visible shuffle
       on every page load). */
    var current = fam.querySelector(".panel-family-sku.is-current");
    if (current && fam.firstElementChild !== current) fam.insertBefore(current, fam.firstElementChild);

    /* Warm EVERY entry, the current one included. Excluding it meant the product
       you arrived on was the only fragment never cached — so stepping to a sibling
       and back was the one hop that paid a cold fetch. Its page being loaded
       doesn't help: the swap consumes the ?view=modal URL, which is a separate
       resource. */
    var famUrls = [];
    fam.querySelectorAll(".panel-family-sku").forEach(function (a) {
      var url = a.getAttribute("data-product-url");
      if (url) famUrls.push(url);

      a.addEventListener("click", function (e) {
        e.preventDefault();
        if (a.classList.contains("is-current")) return;
        /* Move the highlight NOW rather than waiting for applySwapContent, which
           sits behind the fade-out and the fetch — that gap was the lag between
           pressing a SKU and seeing it become active. The swap re-applies the same
           state later, idempotently. */
        fam.querySelectorAll(".panel-family-sku").forEach(function (other) {
          other.classList.toggle("is-current", other === a);
        });
        swapToProduct(url, a.getAttribute("data-handle"), true);
      });
    });

    /* Warm the family — HTML fragment AND its images (see prefetch) — so clicking
       through it is instant. Deferred until the current PDP has finished loading
       so this never competes with the page you're actually viewing. On a soft-swap
       / cached ?view=modal open, load already fired → readyState is "complete" and
       we warm immediately. */
    var warmFamily = function () {
      var jobs = famUrls.map(function (u) { return prefetch(u); });
      /* Instruction media loads LAST, once every sibling fragment has landed —
         see warmInstructionMedia. Settle either way: a failed sibling fetch must
         not leave the instruction images waiting forever. */
      Promise.all(jobs).then(warmInstructionMedia, warmInstructionMedia);
    };
    /* In the index body-swap the PDP fades in over 0.9s and readyState is ALREADY
       "complete" (the index finished loading long ago), so warming here fired N
       fragment fetches + N image decodes on top of the very first frame of that
       fade — the choppy first open. (Opens after it are smooth only because the
       family is warm by then and this does nothing.) Wait for the reveal instead;
       the timeout is the fallback for any path that doesn't emit it. */
    var scheduleWarm = function () {
      if (!document.body.classList.contains("pdp-swap-open")) return warmFamily();
      var ran = false;
      var run = function () {
        if (ran) return;
        ran = true;
        window.removeEventListener("pdp:revealed", run);
        warmFamily();
      };
      window.addEventListener("pdp:revealed", run);
      setTimeout(run, 1200);
    };
    if (document.readyState === "complete") scheduleWarm();
    else window.addEventListener("load", scheduleWarm, { once: true });
  }

  /* Instruction-manual images are emitted loading="lazy" (product-body.liquid) so
     the PDP open only pays for the above-the-fold product row. Lazy alone means
     they never download until you scroll to them; promote them to eager once
     everything ahead of them in the queue is done — the product images, then the
     family fragments and their images — so by the time you scroll down they are
     already there. Idempotent: an image that has already been promoted or loaded
     is skipped.

     Videos keep preload="none" deliberately. They are the heaviest thing on the
     page and, unlike an image, cannot be shown progressively — eagerly pulling
     them would cost far more than it saves. */
  function warmInstructionMedia() {
    document.querySelectorAll(".instruction-media-image").forEach(function (img) {
      if (img.loading === "lazy") img.loading = "eager";
    });
  }

  function modalUrl(url) {
    return url + (url.indexOf("?") >= 0 ? "&" : "?") + "view=modal";
  }

  function prefetch(url) {
    if (!url) return Promise.resolve(null);
    if (prefetchCache[url]) return prefetchCache[url];
    prefetchCache[url] = fetch(modalUrl(url), { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.text() : null; })
      .catch(function () { return null; });
    /* Once the fragment is cached, pull its product images into the browser
       cache too, so the swap's revealSwappedImages() decode resolves instantly on
       click instead of loading images then (the source of the click-through lag). */
    prefetchCache[url].then(function (html) { if (html) warmImages(html); });
    return prefetchCache[url];
  }

  /* ---------- image-area freeze ----------
     The bar and the images are tied together by one number: the images get
     calc(100svh - var(--pdp-peek)), so growing the bar shrinks them in the same
     frame. That coupling is why the bar's resize couldn't simply happen on the
     press — it dragged the outgoing images with it, in plain view.

     Freezing pins the SCROLLER — and only the scroller — to its current pixel
     height for the duration of the swap, unhooking the images from the variable.
     The bar can then take its new height the instant the title changes with the
     images standing still: the row keeps its size and its vertical centring, the
     wrapper's overflow:hidden clips the few px that now fall past its bottom edge,
     and that strip is behind the bar anyway. Released inside applySwapContent
     while the row is at opacity 0, so the images re-centre unseen and are already
     correct when they fade back in.

     .product-page-wrapper is deliberately NOT frozen, and that distinction is the
     whole point. Its height is calc(100svh - var(--pdp-peek)) — the term that
     keeps image area + bar summing to exactly one viewport. Pinning it while the
     bar grew broke that sum: the document became taller than the viewport for the
     ~100ms until release, so the page turned scrollable and then wasn't, which is
     a shift that only ever happens when the bar GROWS. Shrinking never overflows,
     so it looked clean — exactly the asymmetry you described. Leaving the wrapper
     on its calc keeps the document exactly 100svh throughout. */
  function freezeImageArea() {
    if (scrollerEl) scrollerEl.style.height = scrollerEl.getBoundingClientRect().height + "px";
  }

  function releaseImageArea() {
    if (scrollerEl) scrollerEl.style.height = "";
    /* Older builds froze the wrapper too — clear it so a stale inline height from
       a swap that started before this change can't outlive it. */
    var wrapper = document.querySelector(".product-page-wrapper");
    if (wrapper && wrapper.style.height) wrapper.style.height = "";
  }

  /* ---------- swap trace (window.__PDP_DEBUG) ----------
     Samples the bar every frame for 1.5s after a SKU press and logs only the
     frames where something MOVED. It reports the four numbers that can each
     produce a "shift", so the log says which one it actually was:
       h    — the bar's own height (a second value here = a second write)
       top  — the bar's top edge in the viewport (moves if the height changes…
              or if the bar unsticks / the page scrolls, which is a different bug)
       var  — the computed --pdp-peek (the stylesheet's 150 vs a written value)
       wrap — .product-page-wrapper's height, the image area the bar reserves
     Read the log as a timeline: t is ms since the press. */
  /* Debug is opt-in and STICKY so it can be turned on from a phone, where there is
     no console to type into: load any page with ?pdpdebug=1 once (?pdpdebug=0 to
     stop). window.__PDP_DEBUG = true still works on desktop. */
  var pdpDebugOn = (function () {
    try {
      var m = /[?&]pdpdebug=([01])/.exec(window.location.search);
      if (m) localStorage.setItem("pdpDebug", m[1]);
      return localStorage.getItem("pdpDebug") === "1";
    } catch (e) {
      return false;
    }
  })();

  function debugOn() {
    return pdpDebugOn || window.__PDP_DEBUG === true;
  }

  /* The readout, drawn on the page itself — the trace is useless if it only exists
     in a console you can't reach from the device the bug happens on. */
  var traceBox = null;

  function traceLine(text) {
    if (typeof console !== "undefined") console.log("[bar] " + text);
    if (!traceBox) {
      traceBox = document.createElement("div");
      traceBox.id = "pdp-debug-readout";
      document.body.appendChild(traceBox);
    }
    traceBox.textContent = (traceBox.textContent + "\n" + text)
      .split("\n")
      .slice(-14)
      .join("\n");
  }

  function traceBar(label) {
    if (!debugOn() || !panelEl) return;
    var peek = panelEl.querySelector(".product-panel-peek");
    var title = panelEl.querySelector(".product-panel-peek .product-title-container");
    var actions = panelEl.querySelector(".product-panel-actions");
    var wrapper = document.querySelector(".product-page-wrapper");
    if (!peek) return;
    var t0 = performance.now();
    var last = "";
    traceLine("--- " + label + " ---");
    var tick = function () {
      if (!peek.isConnected) return;
      var r = peek.getBoundingClientRect();
      /* Every number that can produce a "shift", so the capture says which one it
         actually was, instead of us inferring it:
           h     bar height          — a second value = a second height write
           top   bar's top edge      — moves on a height change, but ALSO if the
                                       bar unsticks or the page scrolls (other bug)
           var   computed --pdp-peek — stylesheet 150 vs a written value
           ttl   title block height  — the growth we're chasing; if this changes a
                                       frame apart from h, the content and the box
                                       are still being applied in two moments
           act   actions height      — the other half of the bar's content
           wrap  image area height   — frozen during the swap, released after */
      var line =
        "h=" + Math.round(r.height) +
        " top=" + Math.round(r.top) +
        " var=" + getComputedStyle(document.body).getPropertyValue("--pdp-peek").trim() +
        " ttl=" + (title ? Math.round(title.getBoundingClientRect().height) : "-") +
        " act=" + (actions ? Math.round(actions.getBoundingClientRect().height) : "-") +
        " wrap=" + (wrapper ? Math.round(wrapper.getBoundingClientRect().height) : "-");
      if (line !== last) {
        last = line;
        traceLine("t=" + Math.round(performance.now() - t0) + " " + line);
      }
      if (performance.now() - t0 < 1500) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* Mirror each product image from a fetched fragment into a live Image() so the
     browser caches the SAME resource the <img> will later resolve to. Keyed to
     warm each source once. Runs off-screen; failures are ignored. */
  function warmImages(html) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll(".product-page-image").forEach(function (img) {
        var srcset = img.getAttribute("srcset");
        var sizes = img.getAttribute("sizes");
        var src = img.getAttribute("src");
        var key = srcset || src;
        if (!key || warmedImg[key]) return;
        warmedImg[key] = true;
        var pre = new Image();
        if (sizes) pre.sizes = sizes;
        if (srcset) pre.srcset = srcset;
        if (src) pre.src = src;
      });
    } catch (e) {}
  }

  function swapToProduct(url, handle, push) {
    /* Start the fade-out on the CLICK, not on the fetch. The fragment is usually
       warm (initFamily prefetches every sibling), but when it isn't, waiting for
       the response before fading meant the press did nothing at all for the
       length of a round-trip — the title, price and images all sat on the old
       product until it landed. Now the dissolve begins immediately and the fetch
       runs underneath it.

       Both have to finish before the content can be applied: the fade-out so the
       change happens while nothing is visible, the fetch so there is something to
       apply. Whichever is slower gates it — on a warm cache that's the 100ms
       fade, which is the floor we want. */
    traceBar("swap " + handle);

    /* Unhook the images from --pdp-peek BEFORE anything changes, so the bar can
       resize on the press without moving them (see freezeImageArea). */
    freezeImageArea();

    /* Hold routine peek measurement for the whole swap — applyTitleEarly takes the
       one measurement that counts (see syncPeekHeight). */
    peekSyncSuspended = true;

    var fragment = prefetch(url);
    var faded = new Promise(function (resolve) {
      fadeRowOut(resolve, { price: true, title: true });
    });

    /* The title goes in AS SOON AS the fragment is in hand, without waiting for
       the fade-out to elapse with the rest of the content — it was cut on the
       click, so writing its text here is invisible. The peek's HEIGHT is not
       touched here (it used to be): the bar is locked to --pdp-peek, so new text
       alone can't resize it, and the single height write happens at the end of
       applySwapContent once every part of the bar is patched. */
    var parsed = fragment.then(function (html) {
      if (!html) return null;
      var doc = new DOMParser().parseFromString(html, "text/html");
      applyTitleEarly(doc);
      return doc;
    });

    Promise.all([parsed, faded]).then(function (results) {
      var doc = results[0];
      if (!doc) {
        window.location.href = url; /* fallback: hard nav */
        return;
      }
      applySwap(doc, url, handle, push);
    });
  }

  /* Resolve which image set will actually be visible after the swap — mirror
     initVariantSelect()'s preselect order (preserved variant title → #sku hash →
     first variant), then showVariantImages()'s fallback (that variant's own set,
     else the base set). Returns the containers to decode/reveal, so we wait on ONLY
     that set — not every hidden variant set (which delayed the whole content swap). */
  function resolveShownContainers(imagesRoot, newVarData) {
    var ids = Object.keys(newVarData || {});
    var finalId = null;
    if (ids.length) {
      if (lastVariantTitle) {
        var t = lastVariantTitle.trim().toLowerCase();
        finalId = ids.find(function (k) {
          return ((newVarData[k].title || "").trim().toLowerCase() === t);
        }) || null;
      }
      if (!finalId) {
        var hash = window.location.hash.slice(1);
        if (hash) finalId = ids.find(function (k) { return newVarData[k].sku === hash; }) || null;
      }
      if (!finalId) finalId = ids[0];
    }
    var selected = finalId
      ? imagesRoot.querySelectorAll('.img-and-loading-container[data-variant-id="' + finalId + '"]')
      : [];
    if (selected.length) return Array.prototype.slice.call(selected);
    return Array.prototype.slice.call(
      imagesRoot.querySelectorAll('.img-and-loading-container[data-image-base="true"]')
    );
  }

  /* Reveal the just-injected image set without segmentation. The shown containers
     (display != none) are held hidden until EVERY one of their images has decoded
     as a live element, then revealed together in a single frame. Hidden/lazy sets
     get the standard per-image loader so they reveal correctly when later shown. */
  function revealSwappedImages(scroller) {
    /* The indicator does NOT fade for a swap — same rule as a variant switch: the
       row jumps to its new first image, so the fill just moves there with it. */
    var visible = [];
    scroller.querySelectorAll(".img-and-loading-container").forEach(function (c) {
      var img = c.querySelector(".product-page-image");
      if (!img) return;
      var ph = c.querySelector(".image-loading");

      if (c.style.display === "none") {
        // lazy set, shown later — wire the normal loader (mirrors initializeImageLoading)
        if (img.complete && img.naturalWidth > 0) {
          c.classList.remove("is-loading");
          img.style.display = "block";
          if (ph) ph.style.display = "none";
        } else {
          img.style.display = "none";
          // hidden img → container would collapse and swallow the placeholder
          c.classList.add("is-loading");
          if (ph) ph.style.display = "flex";
          if (typeof handleImageLoad === "function") img.addEventListener("load", handleImageLoad);
          if (typeof handleImageError === "function") img.addEventListener("error", handleImageError);
        }
        return;
      }

      // visible set — hold hidden; batch-reveal once all have decoded. Promote lazy
      // imgs (the preserved variant's set is emitted loading="lazy") so decode()
      // actually loads them.
      if (img.loading === "lazy") img.loading = "eager";
      img.style.display = "none";
      c.classList.add("is-loading"); // hold the slot open so the placeholder is visible
      if (ph) ph.style.display = "flex";
      visible.push({ img: img, ph: ph, c: c });
    });

    if (!visible.length) {
      fadeRowIn(); // nothing to wait on — don't strand the row at opacity 0
      return;
    }

    var decodes = visible.map(function (o) {
      return o.img.decode
        ? o.img.decode().catch(function () {})
        : new Promise(function (r) {
            o.img.addEventListener("load", r, { once: true });
            o.img.addEventListener("error", r, { once: true });
          });
    });

    var settled = false;
    var reveal = function () {
      if (settled) return;
      settled = true;
      requestAnimationFrame(function () {
        visible.forEach(function (o) {
          o.c.classList.remove("is-loading");
          o.img.style.display = "block";
          if (o.ph) o.ph.style.display = "none";
        });
        /* The set is now laid out, so scrollWidth is finally real — every earlier
           reading happened while these images were held hidden (collapsed slots).
           Nothing else re-measures once they appear (the ResizeObserver watches the
           scroller's box, which doesn't change here), so do it here: land the row on
           the new product's first image and measure against that. Clearing
           indicatorMeasuring is what unsticks the fill — while it is set,
           updateIndicator bails and the bar keeps the width it had before the
           swap, which is exactly the "stuck where I left it" symptom. */
        if (indicatorSwitchTimer) clearTimeout(indicatorSwitchTimer);
        indicatorMeasuring = false;
        scroller.scrollLeft = 0;
        updateIndicator();
        nudgeIndicator();
        /* The row has been held at opacity 0 since the click (applySwap faded it
           out before injecting) — bring it back now that the new set is laid out. */
        fadeRowIn();
      });
    };
    /* Wait on the TEXT as well as the pixels: a family swap writes the incoming
       product's EN/JP title into the peek, and if its font subset is still in
       flight that bar reflows a beat after the row appears. __textReady is a
       resolved promise once the fonts are in (i.e. every swap after the first),
       so this costs nothing in the common case. */
    if (window.__textReady) decodes.push(window.__textReady);
    Promise.all(decodes).then(reveal);
    setTimeout(reveal, 1500); // never leave the set hidden indefinitely
  }

  /* Instruction-manual images live in the panel body, which is replaced wholesale
     by innerHTML on a family swap. Mirrors initializeImageLoading()'s per-image
     logic: already-decoded → reveal now; still loading → keep the lazy image in
     flow (hiding it would stop lazy-load entirely) and let load/error hide the
     placeholder. */
  function wireInstructionImages(root) {
    root.querySelectorAll(".instruction-media-image").forEach(function (img) {
      var c = img.closest(".img-and-loading-container");
      if (!c) return;
      var ph = c.querySelector(".image-loading");
      if (img.complete && img.naturalWidth > 0) {
        img.style.display = "block";
        if (ph) ph.style.display = "none";
        return;
      }
      if (img.loading !== "lazy") img.style.display = "none";
      if (ph) ph.style.display = "flex";
      if (typeof handleImageLoad === "function") img.addEventListener("load", handleImageLoad);
      if (typeof handleImageError === "function") img.addEventListener("error", handleImageError);
    });
  }

  /* ---------- title in, and the bar's height with it ----------
     The incoming title is written the moment the fragment arrives — for a
     prefetched sibling, the task the press was handled in — and the height it
     needs is measured and applied in that same task. Behind the fade: the title
     was cut to opacity 0 on the press, so what you see is only the box stepping to
     its new size, once. The images can't be dragged along by that step because
     swapToProduct froze the row first (see freezeImageArea).

     Only the title lands here. The actions row is deliberately left to
     applySwapContent, where initVariantSelect() re-wires it in the SAME task —
     injecting it early meant the dropdown showed the incoming product's default
     variant for ~100ms before the preserved colour was restored over it, which is
     a visible text change in the selector. Nothing is lost by waiting: the bar's
     height is a function of the title's wrapping alone (see syncPeekHeight), so
     the actions arriving later cannot move it.

     The height calls are forced past the swap's suspend and the rowFading guard —
     this is the pass those exist to protect. updateCompactPeek runs first: the
     incoming title may be the one that no longer fits beside the buttons
     (peekWouldOverlap), and that decision changes the height measured next. */
  function applyTitleEarly(doc) {
    if (!panelEl) return;

    patchPeekTitle(doc);
    updateCompactPeek({ force: true });
    syncPeekHeight({ force: true });
  }

  function patchPeekTitle(doc) {
    if (!panelEl) return;
    [".product-title-en", ".product-title-jp"].forEach(function (sel) {
      var incoming = doc.querySelector(".product-panel-peek " + sel);
      var live = panelEl.querySelector(".product-panel-peek " + sel);
      if (incoming && live) live.textContent = incoming.textContent.trim();
    });
  }

  /* The actions row (variant selector + add-to-cart). Called from
     applySwapContent, immediately before initVariantSelect() puts the preserved
     variant back — the two belong in one task, or the selector visibly falls back
     to the product's default variant in between. */
  function patchPeekActions(doc) {
    if (!panelEl) return;
    var incoming = doc.querySelector(".product-panel-actions");
    var live = panelEl.querySelector(".product-panel-actions");
    if (incoming && live) live.innerHTML = incoming.innerHTML;
  }

  function applySwap(doc, url, handle, push) {
    var newImages = doc.getElementById("product-images");

    var newVarEl = doc.getElementById("pdp-variants");
    var newVarData = newVarEl ? safeParse(newVarEl.textContent) : {};

    /* The row is ALREADY faded out — swapToProduct started that on the click and
       only calls this once the fade has elapsed, so the sibling's content replaces
       the current one while nothing is visible. fadeRowIn comes later, from
       revealSwappedImages once the incoming set has decoded (it can't be called
       here: the images are held hidden until then, so fading back in now would
       reveal empty slots). Every exit from that function reaches fadeRowIn. */
    if (!panelEl) { fadeRowIn(); return; } // panel closed — don't strand the fade

    applySwapContent(doc, newImages, newVarData, url, handle, push);
  }

  function applySwapContent(doc, newImages, newVarData, url, handle, push) {
    if (!panelEl) { fadeRowIn(); return; } // panel closed mid-fade

    /* Inject the text/panel content immediately (no off-screen image gate — that
       delayed the text). The images reveal together on their own via
       revealSwappedImages once they've decoded. */
    if (newImages && scrollerEl) {
      scrollerEl.innerHTML = newImages.innerHTML;
      /* Pre-apply the FINAL shown set (the preserved variant, or its base fallback)
         so the set we batch-reveal is exactly the one initVariantSelect()'s
         showVariantImages() will settle on. Otherwise we'd reveal the fragment's
         default set, only for it to be swapped a beat later for a hidden (and
         therefore re-segmented) set. */
      var shown = resolveShownContainers(scrollerEl, newVarData);
      if (shown.length) {
        scrollerEl.querySelectorAll(".img-and-loading-container").forEach(function (c) {
          c.style.display = shown.indexOf(c) !== -1 ? "block" : "none";
        });
      }
      /* Batch-reveal the visible set together. initializeImageLoading() reveals each
         image on its own `load` event, so freshly-injected live <img>s (complete=false
         for a beat, even with bytes cached) pop in one by one — the segmented switch.
         revealSwappedImages holds the shown set hidden, waits for ALL of them to
         decode, then shows them in one frame; hidden/lazy sets keep the normal
         per-image loader for when they're shown. */
      revealSwappedImages(scrollerEl);
    } else {
      fadeRowIn(); // no image row came back — don't strand it hidden
    }

    {
      /* variant data (replace the live JSON tag + in-memory map) */
      var newVar = doc.getElementById("pdp-variants");
      var liveVar = document.getElementById("pdp-variants");
      if (newVar && liveVar) liveVar.textContent = newVar.textContent;
      variantData = newVar ? safeParse(newVar.textContent) : {};

      /* main SKU title */
      var newSku = doc.querySelector(".product-panel-sku");
      var liveSku = panelEl.querySelector(".product-panel-sku");
      if (newSku && liveSku) liveSku.textContent = newSku.textContent.trim();

      /* EN/JP type title in the PEEK. Not covered by the body-left replacement
         below — that block sits under the peek, while this pair lives in the
         sticky header, which is why a swap used to leave the previous product's
         title sitting above the new SKU and price.

         Normally a no-op: applyTitleEarly already wrote this the moment the
         fragment arrived. Kept as the backstop for any path that skipped it
         (a panel that was still closed then, a fragment that arrived after). */
      patchPeekTitle(doc);

      /* below-peek content (description, dimensions/weight/mfg, media) */
      var newBody = doc.querySelector(".product-panel-body-left");
      var liveBody = panelEl.querySelector(".product-panel-body-left");
      if (newBody && liveBody) {
        liveBody.innerHTML = newBody.innerHTML;
        /* The instruction-manual media comes in with this innerHTML, so its
           <img>s are brand-new elements with no load listeners — nothing would
           ever hide their .image-loading placeholder (CSS shows it by default),
           leaving the loader spinning on top of a fully-loaded image. Wire them
           the same way initializeImageLoading() does on a hard load. */
        wireInstructionImages(liveBody);
        /* Fresh lazy <img>s arrived with that innerHTML, so warm them too — but
           only after the incoming product row has finished its fade-in, so they
           never compete with the images the swap is actually showing. */
        setTimeout(warmInstructionMedia, WRAP_FADE_IN_MS);
      }

      /* actions (variant selector + add-to-cart, with the new product's ids).
         Stays HERE, a few lines above initVariantSelect(), so the fresh markup and
         the preserved-variant selection land in one task and the dropdown never
         paints the incoming product's default colour in between. */
      patchPeekActions(doc);

      /* price baseline (variant update may refine it) */
      var newPrice = doc.querySelector(".product-panel-price");
      var livePrice = panelEl.querySelector(".product-panel-price");
      if (newPrice && livePrice) livePrice.textContent = newPrice.textContent.trim();

      /* move the family highlight to the new product, but keep the row order fixed —
         only a grid-open leads the row with the clicked SKU (initFamily). Soft-swaps
         between siblings here in the PDP just shift which one is highlighted. */
      var fam = panelEl.querySelector(".product-panel-family");
      if (fam) {
        fam.querySelectorAll(".panel-family-sku").forEach(function (a) {
          a.classList.toggle("is-current", a.getAttribute("data-handle") === handle);
        });
      }

      /* Push the new product entry BEFORE wiring variants — initVariantSelect's
         update() does a replaceState(#sku), which must land on THIS new entry. */
      if (push) history.pushState({ pdpFamily: true, url: url }, "", url);

      /* re-wire the (new) variant select; preselect keeps the chosen colour. For a
         multi-variant product this also calls showVariantImages → refreshIndicator;
         call refreshIndicator here too so single-variant swaps update cleanly. */
      initVariantSelect();

      if (scrollerEl) scrollerEl.scrollLeft = 0;
      window.scrollTo(0, 0);          // show the new product's images from the top
      /* `immediate`: the strict path would set indicatorMeasuring and hold the fill
         until every incoming image reports loaded. The swap holds those images
         hidden until they decode, so any measurement taken here is against
         collapsed slots anyway — and if the wait never resolves, the fill stays
         frozen at its pre-swap width. revealSwappedImages does the authoritative
         measurement once the set is actually laid out. */
      refreshIndicator({ immediate: true });

      /* Backstop, not the main event: applyTitleEarly already set the height from
         the incoming title, on the press. This re-measures now that the family row
         and the actions are patched too, and thanks to the write-on-change guard
         in syncPeekHeight it is a no-op whenever those didn't alter the bar's
         height — which is the normal case, since both are fixed-height rows. If it
         ever does write, __PDP_DEBUG logs it with a second [peek] line and that is
         a bug to chase, not a design.

         Wrapped in __whenTextReady, which is synchronous once the fonts are in. It
         only defers on the first cold swap of a session, and there it defers to
         exactly the moment revealSwappedImages is already waiting for — both gate
         on the same promise — so a fallback-metric correction can never surface
         after the images are back. */
      var measureFinalBar = function () {
        updateCompactPeek({ force: true });
        syncPeekHeight({ force: true });
        /* Hand the images back to calc(100svh - var(--pdp-peek)). Done HERE, with
           the row still at opacity 0 and the incoming set held behind its
           placeholders, so they take their new size unseen and are already correct
           when the fade brings them back. */
        releaseImageArea();
      };
      if (typeof window.__whenTextReady === "function") window.__whenTextReady(measureFinalBar);
      else measureFinalBar();
    }
  }

  /* ---------- instruction-manual videos: click → fullscreen playback ----------

     The videos render WITHOUT a `controls` attribute (product-body.liquid), so
     the whole poster is one click target and there are no native controls to
     compete with this handler. Fullscreen supplies its own controls; exiting
     pauses (see below), which is why losing the inline ones costs nothing.

     Delegated on document and bound once: the family soft-swap replaces
     .product-panel-body-left wholesale, so any per-element binding would be
     thrown away on every sibling switch. */
  function playVideoFullscreen(v) {
    var iosOnly =
      !v.requestFullscreen && !v.webkitRequestFullscreen && !!v.webkitEnterFullscreen;
    /* preload="none" → readyState 0 on the first tap, so nothing can go
       fullscreen yet; the play() below is what starts the fetch. */
    var deferred = iosOnly && v.readyState < 1;

    var enter = function () {
      if (v.requestFullscreen) {
        var p = v.requestFullscreen();
        if (p && p.catch) p.catch(function () {});
      } else if (v.webkitRequestFullscreen) {
        v.webkitRequestFullscreen();
      } else if (v.webkitEnterFullscreen) {
        /* iPhone Safari: no Element.requestFullscreen — only the <video>'s own
           native player, and it refuses until metadata exists. */
        v.webkitEnterFullscreen();
        /* The native player can open paused. A second play() here is allowed
           without a gesture because the element was already unlocked by the
           in-gesture play() below, and it is a no-op if it is already running. */
        var again = v.play();
        if (again && again.catch) again.catch(function () {});
      }
    };

    /* Wire the fullscreen hand-off BEFORE play(), so a fast-arriving
       loadedmetadata can't beat the listener. */
    if (deferred) v.addEventListener("loadedmetadata", enter, { once: true });

    /* play() must be the LAST thing the gesture does. It also kicks off the
       resource load on its own — calling v.load() after it (as this used to)
       resets the element and ABORTS the pending play, which is why iOS landed
       in a fullscreen player that was paused and needed a manual tap. */
    var played = v.play();
    if (played && played.catch) played.catch(function () {});

    if (!deferred) enter();
  }

  /* Leaving fullscreen stops playback — without inline controls a video left
     running behind the page can't be paused. Covers both the standard event and
     iOS's webkitendfullscreen (which fires on the element, not the document). */
  function pauseOnFullscreenExit() {
    if (document.fullscreenElement || document.webkitFullscreenElement) return;
    var vids = document.querySelectorAll(".instruction-media-video");
    for (var i = 0; i < vids.length; i++) {
      if (!vids[i].paused) vids[i].pause();
    }
  }

  function bindInstructionVideos() {
    if (videoListenersBound) return;
    videoListenersBound = true;

    document.addEventListener("click", function (e) {
      var v = e.target.closest && e.target.closest(".instruction-media-video");
      if (!v) return;
      e.preventDefault();
      playVideoFullscreen(v);
    });

    document.addEventListener("fullscreenchange", pauseOnFullscreenExit);
    document.addEventListener("webkitfullscreenchange", pauseOnFullscreenExit);
    document.addEventListener(
      "webkitendfullscreen",
      function (e) {
        if (e.target && e.target.classList.contains("instruction-media-video")) {
          e.target.pause();
        }
      },
      true /* capture: webkitendfullscreen does not bubble */
    );
  }

  function handleFromUrl(url) {
    var m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function onPopState() {
    if (!panelEl) return;
    var url = window.location.pathname + window.location.search;
    var handle = handleFromUrl(url);
    var fam = panelEl.querySelector(".product-panel-family");

    if (fam && handle) {
      var link = fam.querySelector(
        '.panel-family-sku[data-handle="' + handle + '"]'
      );
      if (link) {
        swapToProduct(link.getAttribute("data-product-url"), handle, false);
        return;
      }
    }
    /* outside the family → let the browser load it normally */
    window.location.href = url;
  }

  /* Exposed so the index body-swap can (re)initialize after injecting the panel. */
  window.__pdpInit = initPanel;

  /* Exposed so script-cart.js can hand the button back to its owner after it
     finishes an add (it clears its own in-flight lock on the same button). */
  window.__pdpSyncAddButton = applyButtonState;

  /* This add's request has settled (either way) — drop the optimistic hold it
     took on submit. The unit it stood for is now either in currentCart (the
     cart:updated that preceded this already reconciled it, so this is a no-op)
     or it never happened, in which case the hold has to go or the button stays
     locked on stock that is still purchasable. */
  window.__pdpAddSettled = function (id) {
    releasePending(id);
    applyButtonState();
  };

  /* Shopify REFUSED the add (422 — "you can only add N", or the unit sold out
     from under a page whose inventory snapshot is stale). The count rendered
     into #pdp-variants is now known to be wrong in one specific way: there is
     nothing purchasable beyond what this cart already holds. Clamp qty to that
     and let applyButtonState resolve the label (IN CART if we hold some, SOLD
     OUT if we hold none) instead of leaving the button on ADD TO CART, where
     every further click is another silent 422. */
  window.__pdpAddRejected = function (id) {
    id = String(id);
    delete pendingAdds[id];
    delete pendingBase[id];
    var v = variantData[id];
    if (v) {
      v.qty = cartOnlyQtyFor(id);
      v.available = false;
      v.rejected = true;
    }
    applyButtonState();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPanel);
  } else {
    initPanel();
  }
})();
